/**
 * Vanilla searchable picker (combobox)
 *
 * One module drives BOTH the Country and the Preferred language fields on the
 * two registration forms. It is parameterised entirely by the markup it mounts
 * onto: the ERB partial server-renders every option (label + `data-value` code)
 * plus the quick-select shortcut, so no dataset is ever duplicated in TS.
 *
 * Behavior (mirrors the marketing site's country picker):
 *   - closed control shows the committed label; focus/click clears it so the
 *     "Type to search…" placeholder shows and opens the panel
 *   - a QUICK SELECT group sits above the full list, visible ONLY while the
 *     query is empty
 *   - filtering is a case-insensitive substring match on the visible label
 *   - ArrowDown / ArrowUp move the highlight, Enter commits, Escape closes,
 *     a click outside closes; closing without a commit restores the label
 *   - the committed value written to the hidden input is always the stable
 *     code ("US" / "en"), never the display label
 *
 * Markup contract (see app/views/users/shared/_registration_profile_fields.html.erb):
 *   [data-registration-picker]      root
 *     [data-picker-input]           the role="combobox" text input
 *     [data-picker-value]           the hidden input carrying the code
 *     [data-picker-listbox]         the role="listbox" panel
 *       [data-picker-quick-select]  the role="group" shortcut section
 *       [data-picker-option]        every option, carrying data-value
 *       [data-picker-empty]         the "no matches" row
 */

const HIGHLIGHT_CLASSES = ["bg-tint-primary-bg-soft", "text-text-link"];

export interface SearchablePickerHandle {
  /** Tear down every listener this picker registered. */
  destroy: () => void;
}

/**
 * Mount every picker found inside a scope.
 *
 * @param scope - the element or document to search; defaults to `document`
 * @returns one handle per mounted picker (empty when the page has none)
 */
export function mountSearchablePickers(
  scope: ParentNode = document,
): SearchablePickerHandle[] {
  const roots = Array.from(
    scope.querySelectorAll<HTMLElement>("[data-registration-picker]"),
  );
  return roots
    .map((root) => mountSearchablePicker(root))
    .filter((handle): handle is SearchablePickerHandle => handle !== null);
}

/**
 * Mount a single picker onto its server-rendered root.
 *
 * @param root - the `[data-registration-picker]` element
 * @returns a handle, or `null` when the root is missing a required child
 */
export function mountSearchablePicker(
  root: HTMLElement,
): SearchablePickerHandle | null {
  const input = root.querySelector<HTMLInputElement>("[data-picker-input]");
  const valueInput = root.querySelector<HTMLInputElement>(
    "[data-picker-value]",
  );
  const listbox = root.querySelector<HTMLElement>("[data-picker-listbox]");
  if (!input || !valueInput || !listbox) return null;

  const quickSelect = listbox.querySelector<HTMLElement>(
    "[data-picker-quick-select]",
  );
  const emptyRow = listbox.querySelector<HTMLElement>("[data-picker-empty]");
  const options = Array.from(
    listbox.querySelectorAll<HTMLElement>("[data-picker-option]"),
  );

  let isOpen = false;
  let highlightIndex = -1;
  let committedLabel = input.value;

  /**
   * The options a keyboard user can currently reach: not filtered out, and not
   * sitting inside the hidden QUICK SELECT group.
   *
   * @returns the reachable options in DOM order
   */
  function reachableOptions(): HTMLElement[] {
    return options.filter((option) => {
      if (option.hidden) return false;
      const section = option.closest<HTMLElement>("[data-picker-quick-select]");
      return !section || !section.hidden;
    });
  }

  /**
   * Apply the QUICK SELECT gate and the substring filter for a query.
   *
   * @param query - the raw text typed by the user
   */
  function applyFilter(query: string): void {
    const needle = query.trim().toLowerCase();
    if (quickSelect) quickSelect.hidden = needle.length > 0;

    options.forEach((option) => {
      if (option.closest("[data-picker-quick-select]")) return;
      const label = (option.textContent ?? "").trim().toLowerCase();
      option.hidden = needle.length > 0 && !label.includes(needle);
    });

    if (emptyRow) emptyRow.hidden = reachableOptions().length > 0;
  }

  /**
   * Move the visual highlight (and `aria-activedescendant`) to an option.
   *
   * @param index - index into `reachableOptions()`, or -1 to clear
   */
  function setHighlight(index: number): void {
    options.forEach((option) => {
      option.removeAttribute("data-highlighted");
      option.classList.remove(...HIGHLIGHT_CLASSES);
    });

    const reachable = reachableOptions();
    highlightIndex = reachable.length === 0 ? -1 : index;

    const current = highlightIndex >= 0 ? reachable[highlightIndex] : undefined;
    if (!current) {
      input.removeAttribute("aria-activedescendant");
      return;
    }

    current.setAttribute("data-highlighted", "true");
    current.classList.add(...HIGHLIGHT_CLASSES);
    input.setAttribute("aria-activedescendant", current.id);
    // jsdom does not implement scrollIntoView; the guard keeps unit tests green.
    if (typeof current.scrollIntoView === "function") {
      current.scrollIntoView({ block: "nearest" });
    }
  }

  /**
   * Step the highlight, wrapping at both ends.
   *
   * @param delta - +1 for ArrowDown, -1 for ArrowUp
   */
  function moveHighlight(delta: number): void {
    const count = reachableOptions().length;
    if (count === 0) return;
    const next =
      highlightIndex < 0
        ? delta > 0
          ? 0
          : count - 1
        : (highlightIndex + delta + count) % count;
    setHighlight(next);
  }

  /** Open the panel, clearing the text so the search placeholder shows. */
  function open(): void {
    if (isOpen) return;
    isOpen = true;
    input.value = "";
    listbox.hidden = false;
    input.setAttribute("aria-expanded", "true");
    applyFilter("");
    setHighlight(-1);
  }

  /** Close the panel and restore the committed label into the text input. */
  function close(): void {
    if (!isOpen) return;
    isOpen = false;
    listbox.hidden = true;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
    setHighlight(-1);
    applyFilter("");
    input.value = committedLabel;
  }

  /**
   * Persist an option: the code goes to the hidden input, the label to the
   * visible one.
   *
   * @param option - the option element to commit
   */
  function commit(option: HTMLElement): void {
    const code = option.dataset.value ?? "";
    committedLabel = (option.textContent ?? "").trim();
    valueInput.value = code;
    options.forEach((candidate) => {
      candidate.setAttribute(
        "aria-selected",
        candidate.dataset.value === code ? "true" : "false",
      );
    });
    close();
  }

  const onFocus = () => open();

  const onPointerDown = () => open();

  const onInput = () => {
    if (!isOpen) open();
    applyFilter(input.value);
    setHighlight(-1);
  };

  const onKeydown = (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!isOpen) {
          open();
          return;
        }
        moveHighlight(1);
        return;
      case "ArrowUp":
        event.preventDefault();
        if (!isOpen) {
          open();
          return;
        }
        moveHighlight(-1);
        return;
      case "Enter": {
        if (!isOpen) return;
        // Never let Enter submit the form while the panel is open.
        event.preventDefault();
        const reachable = reachableOptions();
        const target =
          highlightIndex >= 0
            ? reachable[highlightIndex]
            : reachable.length === 1
              ? reachable[0]
              : undefined;
        if (target) commit(target);
        else close();
        return;
      }
      case "Escape":
        if (!isOpen) return;
        event.preventDefault();
        close();
        return;
      case "Tab":
        close();
        return;
      default:
    }
  };

  const onListboxMousedown = (event: MouseEvent) => {
    // Keep focus on the input so the click-to-commit below still sees it open.
    event.preventDefault();
  };

  const onListboxClick = (event: MouseEvent) => {
    const option = (event.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-picker-option]",
    );
    if (option) commit(option);
  };

  const onDocumentPointerDown = (event: Event) => {
    if (!isOpen) return;
    const target = event.target as Node | null;
    if (target && root.contains(target)) return;
    close();
  };

  input.addEventListener("focus", onFocus);
  input.addEventListener("pointerdown", onPointerDown);
  input.addEventListener("input", onInput);
  input.addEventListener("keydown", onKeydown);
  listbox.addEventListener("mousedown", onListboxMousedown);
  listbox.addEventListener("click", onListboxClick);
  document.addEventListener("pointerdown", onDocumentPointerDown, true);
  document.addEventListener("click", onDocumentPointerDown, true);

  return {
    destroy: () => {
      input.removeEventListener("focus", onFocus);
      input.removeEventListener("pointerdown", onPointerDown);
      input.removeEventListener("input", onInput);
      input.removeEventListener("keydown", onKeydown);
      listbox.removeEventListener("mousedown", onListboxMousedown);
      listbox.removeEventListener("click", onListboxClick);
      document.removeEventListener("pointerdown", onDocumentPointerDown, true);
      document.removeEventListener("click", onDocumentPointerDown, true);
    },
  };
}
